import { BaseScreen, Device } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { RadioSelectorStyles } from '../ui/RadioSelector';
import { getCountryFlag } from '../ui/TextConfig';
import { FlagIcon } from './../ui/FlagIcon';
import { FlagSelector } from './../ui/FlagSelector';
import { Panel } from './../ui/Panel';
export class FlagSelectScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public confirmText!: LocalisedText;
    public backButton!: NineSliceButton;
    public settingsPanel!: Panel;
    public container!: Container;

    public flagSelect!: FlagSelector;
    public _firstTime: boolean;
    public _backToHome: boolean;
    public _proceedToHome: boolean;

    public leftBtn!: NineSliceButton;
    public rightBtn!: NineSliceButton;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this._firstTime = false;
        this._backToHome = false;
        this._proceedToHome = false;
    }

    async init(): Promise<void>
    {
        const btnWidth = 300;
        const btnHeight = 85;
        const btnFontSize = 32;
        const btnFontStrokeThickness = 6;

        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0.1;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.container = new Container();
        this.view.addChild(this.container);

        this.settingsPanel = new Panel({
            bg: 'panel-license',
            w: 820,
            h: 466,
        });
        this.container.addChild(this.settingsPanel.view);

        this.mountDecoration();

        this.title = new LocalisedText('flagSelectScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 56,
            maxWidth: 650,
        });
        this.title.anchor.set(0.5);
        this.settingsPanel.view.addChild(this.title);
        this.title.y = (-this.settingsPanel.bg.height / 2) + 80;

        const confirmIcon = new Sprite();

        this.confirmText = new LocalisedText('flagSelectScreen_confirm', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: btnFontSize,
            maxWidth: 250,
        });

        this.confirmText.anchor.set(0.5);
        confirmIcon.addChild(this.confirmText);

        const confirmButton = new NineSliceButton({
            icon: confirmIcon,
            bgAsset: 'button-ninesliced-magenta',
            w: btnWidth,
            h: btnHeight,
        });

        confirmButton.view.y = (this.settingsPanel.bg.height / 2) + 55;

        this.settingsPanel.view.addChild(confirmButton.view);
        confirmButton.onPress.connect(() =>
        {
            Shared.settings.playerFlag = Shared.assets.availableFlags[this.flagSelect.radioSelector.getActiveIndex()];
            if (Shared.game) Shared.game.hud.nicknameWidget.refresh();
            this.toNextScreen();
        });

        this.flagSelect = new FlagSelector({
            buttonType: FlagIcon as any,
            style: RadioSelectorStyles.inlineWrap,
            padding: 6,
            inlineWrapLimit: 300,
            buttons: this._createFlagButtonMeta(),
            containerHeight: 290,
            width: 300,
            height: 500,
        });
        this.flagSelect.view.position.set(0, 70);
        this.settingsPanel.view.addChild(this.flagSelect.view);

        const leftArrow = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        leftArrow.rotation = Math.PI * 0.5;
        leftArrow.scale.x *= 1.2;
        leftArrow.scale.y *= 1.2;
        this.leftBtn = new NineSliceButton({
            icon: leftArrow,
            w: 80,
            h: 220,
        });
        this.leftBtn.view.visible = !Device.mobile;
        this.leftBtn.onPress.connect(() => this.flagSelect.moveDown());
        this.leftBtn.view.position.set(-330, 40);

        this.settingsPanel.view.addChild(this.leftBtn.view);

        const rightArrow = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        rightArrow.rotation = Math.PI * -0.5;
        rightArrow.scale.x *= 1.2;
        rightArrow.scale.y *= 1.2;
        this.rightBtn = new NineSliceButton({
            icon: rightArrow,
            w: 80,
            h: 220,
        });
        this.rightBtn.view.visible = !Device.mobile;
        this.rightBtn.view.position.set(330, 40);
        this.rightBtn.onPress.connect(() => this.flagSelect.moveUp());

        this.settingsPanel.view.addChild(this.rightBtn.view);

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() => this.toPrevScreen());
    }

    private mountDecoration(): void
    {
        const wheel = new Sprite(Shared.assets.getTexture('license-wheel'));

        wheel.anchor.set(0.5);
        wheel.x = (this.settingsPanel.bg.width / 2) - 138;
        wheel.y = 50;
        this.settingsPanel.view.addChild(wheel);

        const track = new Sprite(Shared.assets.getTexture('tiretrack'));

        track.anchor.set(0.5);
        track.x = -(this.settingsPanel.bg.width / 2) + 140;
        track.y = 0;
        this.settingsPanel.view.addChild(track);

        const lanyard = new Sprite(Shared.assets.getTexture('license-lanyard'));

        lanyard.anchor.set(0.5, 1);
        lanyard.y = (-this.settingsPanel.bg.height / 2) + 45;
        this.settingsPanel.view.addChild(lanyard);

        const vehicles = new Sprite(Shared.assets.getTexture('license-vehicles'));

        vehicles.anchor.set(0.5);
        vehicles.x = (this.settingsPanel.bg.width / 2) - 100;
        vehicles.y = -(this.settingsPanel.bg.height / 2) + 40;
        this.settingsPanel.view.addChild(vehicles);
    }

    async prepare(screenData: Record<string, any>): Promise<void>
    {
        this._firstTime = screenData.firstTime ?? false;
        this._backToHome = screenData.backToHome ?? false;
        this._proceedToHome = screenData.proceedToHome ?? false;
    }

    public update(delta = 1): void
    {
        this.flagSelect.update(delta);
        this.app.clouds?.update(delta);
    }

    show(): void
    {
        this.resize(this.width, this.height);

        // If first time, use flag correspondent to language
        if (this._firstTime)
        {
            Shared.settings.playerFlag = getCountryFlag(Shared.location.countryId);
        }

        // If somehow selected flag is not available, fallback to allstar one
        if (!Shared.assets.availableFlags.includes(Shared.settings.playerFlag))
        {
            Shared.settings.playerFlag = 'allstar-1';
        }

        this.flagSelect.radioSelector.setSelected(Shared.assets.availableFlags.indexOf(Shared.settings.playerFlag));
        this.flagSelect.snapToSelected();

        this.view.visible = true;
        this.backButton.view.visible = !this._firstTime;

        gsap.killTweensOf(this.container);
        this.container.y = -200;
        this.container.rotation = -Math.PI * 0.3;
        gsap.to(this.container, { rotation: 0, duration: 2, ease: 'elastic.out' });
        Shared.game.soundSystem.playSfx('windmill');
    }

    async hide(): Promise<void>
    {
        gsap.killTweensOf(this.container);
        await gsap.to(this.container, { y: -1000, duration: 0.5, ease: 'back.in' });
    }
    
    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.container.x = this.app.stageCenter.x;
        this.container.y = -200;
        this.settingsPanel.view.y = this.app.stageCenter.y + 200;

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );
        
        this.app.clouds?.resize(w, h);
    }

    private toPrevScreen(): void
    {
        if (this._backToHome)
        {
            this.app.controller.toHome();
        }
        else
        {
            this.app.controller.toChangeName(this._firstTime, this._backToHome);
        }
    }

    private toNextScreen(): void
    {
        if (this._backToHome || this._proceedToHome)
        {
            this.app.controller.toHome();
        }
        else if (!this._firstTime)
        {
            this.app.controller.toSettings();
        }
        else
        {
            this.app.controller.toSplash(true);
        }
    }

    _createFlagButtonMeta(): any[]
    {
        const flagMeta = [] as any[];
        const value = Shared.assets.availableFlags.indexOf(Shared.settings.playerFlag);

        Shared.assets.availableFlags.forEach((id, index) =>
        {
            flagMeta.push({
                id,
                index,
                active: value === index,
                selected: value === index,
                texture: `flag-${id}`,
                onClick: () => { this.flagSelect.radioSelector.setSelected(index); },
            });
        });

        return flagMeta;
    }
}
